// @ts-nocheck
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from 'lucide-react';
import { useAdminAuth } from '@/auth/AuthProvider';
import { validatePassword } from '@staty/shared/utils/passwordValidator';
import AuthShell, {
  AuthHeaderSwap,
  authInput,
  authLabel,
  authPrimaryButton,
  authErrorBanner,
} from '@/components/Auth/AuthShell';
import { trackEvent } from '@/utils/analytics';
import GoogleSignInButton from '@/components/Auth/GoogleSignInButton';
import { decodePendingStructure, PENDING_STRUCTURE_KEY } from '@/components/CreateOrganization/PendingDescriptionBanner';

// Self-registration: create a new admin (no org) → /setup to create/join one.
// Invites are handled separately via the emailed /reset-password link.
// INT-0038: three fields (name, email, password) + Google. The 5-field form
// with a confirm box lost ~40% of visitors who opened it (GA4, 2026-08-24 →
// 2026-10-12); the API splits `name` on the first space.
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const ERRORS = {
  name: 'Please enter your name.',
  email: 'Please enter a valid email.',
  password: 'Your password does not meet the rules above.',
};
function SignUpPage() {
  const navigate = useNavigate();
  const { register } = useAdminAuth();

  // Landing AI-teaser carry-through: staty.io passes ?league_desc= on the
  // sign-up link; stash it (localStorage survives the whole signup → create-org
  // hop) for GetStartedAdmin to pre-fill the AI builder with.
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const desc = params.get('league_desc');
      if (desc && desc.trim()) localStorage.setItem('staty-pending-league-desc', desc.trim().slice(0, 500));
      // INT-0011: the landing widget's sport chip rides along so the create
      // wizard can preselect it.
      const sport = params.get('sport');
      if (sport && /^[a-z-]{2,30}$/.test(sport)) localStorage.setItem('staty-pending-league-sport', sport);
      // INT-0011 tier 2: the previewed structure itself (base64url JSON).
      // Fail-closed — a corrupt param is dropped and the confirm step
      // regenerates from the description instead.
      const structureParam = params.get('structure');
      if (structureParam) {
        const structure = decodePendingStructure(structureParam);
        if (structure) localStorage.setItem(PENDING_STRUCTURE_KEY, JSON.stringify(structure));
      }
      // Acquisition attribution (funnel analytics 2026-09-14): the landing
      // forwards first-touch ad/CTA params onto the sign-up URL; stash them
      // FIRST-TOUCH-ONLY so AuthProvider can attach them to the register /
      // google-auth payload. Server re-sanitizes and stamps capturedAt.
      if (!localStorage.getItem('staty-acquisition')) {
        const ACQ_PARAM_TO_FIELD: Record<string, string> = {
          utm_source: 'utmSource',
          utm_medium: 'utmMedium',
          utm_campaign: 'utmCampaign',
          utm_term: 'utmTerm',
          utm_content: 'utmContent',
          gclid: 'gclid',
          fbclid: 'fbclid',
          ref: 'ref',
          cta: 'cta',
          landing_path: 'landingPath',
        };
        const acquisition: Record<string, string> = {};
        Object.entries(ACQ_PARAM_TO_FIELD).forEach(([param, field]) => {
          const v = params.get(param);
          if (v && v.trim()) acquisition[field] = v.trim().slice(0, 200);
        });
        if (Object.keys(acquisition).length) {
          localStorage.setItem('staty-acquisition', JSON.stringify(acquisition));
        }
      }
    } catch { /* ignore */ }
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [fieldError, setFieldError] = useState<{ field: 'name' | 'email' | 'password'; message: string } | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const pwCheck = useMemo(() => validatePassword(form.password), [form.password]);

  const update = (key: 'name' | 'email' | 'password') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((p) => ({ ...p, [key]: value }));
    if (fieldError && fieldError.field === key) setFieldError(null);
  };

  const fail = (field: 'name' | 'email' | 'password', ref: React.RefObject<HTMLInputElement>) => {
    // GA4 funnel diagnostics (INT-0038): WHICH field blocks sign-ups. Never a value.
    trackEvent('sign_up_error', { field });
    setFieldError({ field, message: ERRORS[field] });
    ref.current?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!form.name.trim()) return fail('name', nameRef);
    if (!EMAIL_RE.test(form.email.trim())) return fail('email', emailRef);
    if (!pwCheck.valid) return fail('password', passwordRef);
    setIsSubmitting(true);
    trackEvent('sign_up_started', { method: 'password' });
    try {
      await register({ name: form.name.trim(), email: form.email.trim(), password: form.password });
      trackEvent('sign_up', { method: 'password' });
      navigate('/setup', { replace: true });
    } catch (err: any) {
      trackEvent('sign_up_error', { field: 'server' });
      const msg = (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Sign up failed.';
      setErrorMessage(String(msg));
    } finally {
      setIsSubmitting(false);
    }
  };

  const inlineError = (field: 'name' | 'email' | 'password') =>
    fieldError && fieldError.field === field ? (
      <div className="mt-1 text-xs font-medium text-red-600" role="alert">{fieldError.message}</div>
    ) : null;

  return (
    <AuthShell
      headerAction={<AuthHeaderSwap prompt="Already have an account?" to="/sign-in" label="Sign in" />}
      title={<>Create your <span className="text-ploy-accent-primary-500">account</span></>}
      subtitle="Set up Staty to manage your leagues."
    >
      {errorMessage && <div className={authErrorBanner}>{errorMessage}</div>}
      <GoogleSignInButton onError={setErrorMessage} intent="sign-up" />
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="sign-up-name" className={authLabel}>Your name</label>
          <input
            id="sign-up-name" ref={nameRef} type="text" autoComplete="name" value={form.name}
            onChange={update('name')} className={authInput} placeholder="Jordan Lee"
          />
          {inlineError('name')}
        </div>
        <div>
          <label htmlFor="sign-up-email" className={authLabel}>Email</label>
          <input
            id="sign-up-email" ref={emailRef} type="email" autoComplete="email" value={form.email}
            onChange={update('email')} className={authInput} placeholder="jordan@example.com"
          />
          {inlineError('email')}
        </div>
        <div>
          <label htmlFor="sign-up-password" className={authLabel}>Password</label>
          <div className="relative">
            <input
              id="sign-up-password" ref={passwordRef} type={showPassword ? 'text' : 'password'}
              autoComplete="new-password" value={form.password} onChange={update('password')}
              className={`${authInput} pr-12`} placeholder="Create a password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              data-tour="sign-up-toggle-password"
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-ploy-neutral-inverse-500 hover:text-ploy-neutral-inverse-800"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {inlineError('password')}
        </div>
        {form.password.length > 0 && (
          <ul className="space-y-1 rounded-lg border border-ploy-accent-primary-100 bg-ploy-accent-primary-50 p-3">
            {pwCheck.results.map((r) => (
              <li key={r.key} className={`flex items-center gap-2 text-xs ${r.met ? 'text-ploy-accent-primary-600' : 'text-ploy-neutral-inverse-500'}`}>
                <span className="w-3 text-center">{r.met ? '✓' : '○'}</span>{r.label}
              </li>
            ))}
          </ul>
        )}
        <button type="submit" disabled={isSubmitting} data-tour="sign-up-submit" className={`${authPrimaryButton} mt-2`}>
          {isSubmitting ? 'Creating…' : 'Create account'}
        </button>
      </form>
    </AuthShell>
  );
}

export default SignUpPage;
